import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import useEscape from 'hooks/use-escape';
import Icon from 'components/icon';

// The BankID / manuell-registrering choice modal. Shared by the register-member CTA
// block and the floating "Bli medlem" support button so the modal lives in one
// place. Controlled — the parent owns `open` and `onClose`. Each choice navigates
// to the member-registration island, which reads the ?method marker.
//
// NOTE: intentionally keeps the `register-member-cta__*` class names so the existing
// (globally bundled) register-member-cta.scss styles it — no CSS duplication.

// A campaign CTA can carry a campaign code (kampanjekode) into the form: it's added
// as `?kode=` and the island captures + persists it (survives the BankID round-trip).
const formUrl = (method, campaignCode) => {
  const base = `/bli-medlem?method=${method}`;
  return campaignCode
    ? `${base}&kode=${encodeURIComponent(campaignCode)}`
    : base;
};

const RegisterMemberModal = ({ open, onClose, title, campaignCode }) => {
  const modalRef = useRef(null);

  // Close on Escape.
  useEscape(onClose);

  // Close on a click outside the modal. Attached only while open and deferred one
  // tick so the same click that opened it doesn't immediately close it.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onDocMouseDown = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const timer = window.setTimeout(
      () => document.addEventListener('mousedown', onDocMouseDown),
      0
    );

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [open, onClose]);

  const navigateTo = url => () => {
    if (typeof window !== 'undefined') {
      window.location.assign(url);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div className="register-member-cta__overlay">
      <div
        ref={modalRef}
        className="register-member-cta__modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <button
          type="button"
          className="register-member-cta__close"
          aria-label="Lukk"
          onClick={onClose}
        >
          ×
        </button>

        <h2 className="register-member-cta__title">{title}</h2>

        <div className="register-member-cta__content">
          <button
            type="button"
            className="register-member-cta__button register-member-cta__button--bankid"
            onClick={navigateTo(formUrl('bankid', campaignCode))}
          >
            <span className="register-member-cta__icon">
              <Icon name="bankid" />
            </span>
            <span className="register-member-cta__label">
              Fortsett med BankID
            </span>
          </button>

          <button
            type="button"
            className="register-member-cta__button register-member-cta__button--secondary"
            onClick={navigateTo(formUrl('manual', campaignCode))}
          >
            <span className="register-member-cta__icon">
              <Icon name="pencil" />
            </span>
            <span className="register-member-cta__label">
              Registrer deg manuelt
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

RegisterMemberModal.defaultProps = {
  title: 'Bli medlem',
  campaignCode: null
};

RegisterMemberModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  campaignCode: PropTypes.string
};

export default RegisterMemberModal;
